import React from "react";
import { Scale, Briefcase, Users, Building2, Key, HardHat, Handshake, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const mediationAreas = [{
  title: "İşçi ve İşveren Uyuşmazlıkları",
  description: "Kıdem tazminatı, ihbar tazminatı, fazla mesai ve işe iade taleplerinde dava sürecine girmeden hızlı ve kesin çözüm.",
  icon: <HardHat className="size-8" />,
  link: "https://celikhukuk.net/arabuluculuk/"
}, {
  title: "Ticari Uyuşmazlıklar",
  description: "Şirketler arası alacak verecek davaları, sözleşme ihlalleri ve ticari tazminat konularında gizlilik esaslı anlaşma zemini.",
  icon: <Briefcase className="size-8" />,
  link: "https://celikhukuk.net/arabuluculuk/"
}, {
  title: "Kira ve Gayrimenkul",
  description: "Kira tespiti, tahliye süreçleri ve kat mülkiyeti kaynaklı anlaşmazlıklarda tarafları koruyan barışçıl çözümler.",
  icon: <Key className="size-8" />,
  link: "https://celikhukuk.net/arabuluculuk/"
}, {
  title: "Tüketici Hukuku",
  description: "Ayıplı mal ve hizmetlerden doğan uyuşmazlıklarda, tüketici hakem heyeti veya mahkeme öncesi pratik sonuçlar.",
  icon: <Scale className="size-8" />,
  link: "https://celikhukuk.net/arabuluculuk/"
}, {
  title: "İnşaat ve Eser Sözleşmeleri",
  description: "Arsa payı karşılığı inşaat sözleşmeleri ve eksik ifa durumlarında teknik detaylara boğulmadan uzlaşma imkanı.",
  icon: <Building2 className="size-8" />,
  link: "https://celikhukuk.net/arabuluculuk/"
}, {
  title: "Aile Hukuku (Mal Rejimi)",
  description: "Boşanma sonrası mal paylaşımı, katkı payı ve katılma alacağı gibi maddi konularda tarafları yıpratmayan süreçler.",
  icon: <Users className="size-8" />,
  link: "https://celikhukuk.net/arabuluculuk/"
}];
const benefits = [{
  title: "Hız ve Verimlilik",
  description: "Yıllar süren davalar yerine haftalar içinde sonuç.",
  icon: <Clock className="size-5 text-primary" />
}, {
  title: "Gizlilik İlkesi",
  description: "Tüm görüşmeler ve belgeler kesinlikle gizli tutulur.",
  icon: <ShieldCheck className="size-5 text-primary" />
}, {
  title: "Ekonomik Çözüm",
  description: "Dava masraflarına kıyasla çok daha düşük maliyet.",
  icon: <Handshake className="size-5 text-primary" />
}];
export default function ArabuluculukAlanlari() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="arabuluculuk-alanları" className="dark bg-[#0a0f1a] text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-primary/20 rounded-full blur-[120px]" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] bg-secondary/20 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight text-[#cbd5e1]">
            Uzman Arabuluculuk Alanlarımız
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed font-sans">
            Yargı süreçlerinin ağırlığı altında ezilmek yerine, uzman arabuluculuk hizmetlerimizle 
            uyuşmazlıklarınızı <span className="text-foreground font-medium">hızlı</span>, <span className="text-foreground font-medium">gizli</span> ve <span className="text-foreground font-medium">ekonomik</span> bir şekilde çözüme kavuşturuyoruz.
          </p>
          
          {}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-center gap-2 bg-[#c5a059]/10 border-[#c5a059]/30 px-4 py-2 rounded-full backdrop-blur-sm">
                {benefit.icon}
                <span className="text-sm font-medium">{benefit.title}</span>
              </div>)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {mediationAreas.map((area, i) => <div key={i} data-index={i} className={`transition-all duration-700 delay-[${i * 100}ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Card className="h-full border-border/60 bg-card/40 backdrop-blur-sm hover:bg-card/80 hover:border-[#c5a059]/30 transition-all duration-300 group flex flex-col text-center items-center">
                <CardHeader className="pb-2 pt-8 flex flex-col items-center w-full">
                  <div className="mb-6 p-4 rounded-full bg-[#c5a059]/10 text-[#c5a059] group-hover:bg-[#c5a059] group-hover:text-white transition-colors duration-300">
                    {area.icon}
                  </div>
                  <CardTitle className="text-xl font-serif font-semibold mb-2 text-[#cbd5e1] group-hover:text-[#c5a059] transition-colors">
                    {area.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow px-8">
                  <CardDescription className="text-base leading-relaxed font-sans">
                    {area.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pb-8 pt-4">
                  <Button variant="ghost" className="group/btn text-primary hover:text-primary hover:bg-primary/10" asChild>
                    <Link to={area.link}>
                      Detaylı Bilgi <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>

        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Button size="lg" className="px-8 py-6 text-lg shadow-lg shadow-primary/20" asChild>
            <Link to="https://celikhukuk.net/iletisim/">
              Hemen Başvurun
            </Link>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            İlk görüşme ve değerlendirme için randevu alabilirsiniz.
          </p>
        </div>
      </div>
    </section>;
}